import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Avatar } from 'primereact/avatar';
import { Menu } from 'primereact/menu';
import { taskNotificationsAPI, TaskNotification } from '../services/api';
import { showCustomAlert } from './CustomAlert';
import './TaskNotifications.css';

const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const typeLabels: Record<string, string> = {
  task_assigned: 'Zadatak dodeljen',
  task_updated: 'Zadatak ažuriran',
  task_completed: 'Zadatak završen',
  task_deleted: 'Zadatak obrisan',
  task_status_changed: 'Status promenjen',
  project_assigned: 'Projekat dodeljen',
  project_status_changed: 'Status projekta promenjen',
  comment_added: 'Novi komentar',
  procurement_request: 'Zahtev za nabavku',
  procurement_approved: 'Zahtev odobren',
  procurement_rejected: 'Zahtev odbijen',
  procurement_delivered: 'Roba isporučena',
  procurement_verified: 'Isporuka potvrđena',
  procurement_delivery_rejected: 'Isporuka odbijena',
  announcement_published: 'Obaveštenje objavljeno'
};

const typeSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
  task_assigned: 'info',
  task_updated: 'info',
  task_completed: 'success',
  task_deleted: 'danger',
  task_status_changed: 'warning',
  project_assigned: 'info',
  project_status_changed: 'warning',
  comment_added: 'info',
  procurement_request: 'info',
  procurement_approved: 'success',
  procurement_rejected: 'danger',
  procurement_delivered: 'success',
  procurement_verified: 'success',
  procurement_delivery_rejected: 'danger',
  announcement_published: 'info'
};

interface TypeStyle {
  icon: string;
  bg: string;
  color: string;
}

const typeStyle = (type: string): TypeStyle => {
  if (type.startsWith('task_')) {
    if (type === 'task_completed') return { icon: 'pi pi-check-circle', bg: '#dcfce7', color: '#16a34a' };
    if (type === 'task_deleted') return { icon: 'pi pi-times-circle', bg: '#fee2e2', color: '#dc2626' };
    if (type === 'task_status_changed') return { icon: 'pi pi-sync', bg: '#fef3c7', color: '#d97706' };
    return { icon: 'pi pi-check-square', bg: '#dbeafe', color: '#2563eb' };
  }
  if (type.startsWith('project_')) {
    return { icon: 'pi pi-briefcase', bg: '#ede9fe', color: '#7c3aed' };
  }
  if (type === 'comment_added') {
    return { icon: 'pi pi-comments', bg: '#cffafe', color: '#0891b2' };
  }
  if (type.startsWith('procurement_')) {
    if (type.includes('approved') || type.includes('verified') || type.includes('delivered')) {
      return { icon: 'pi pi-shopping-cart', bg: '#dcfce7', color: '#16a34a' };
    }
    if (type.includes('rejected')) {
      return { icon: 'pi pi-shopping-cart', bg: '#fee2e2', color: '#dc2626' };
    }
    return { icon: 'pi pi-shopping-cart', bg: '#fef3c7', color: '#d97706' };
  }
  if (type === 'announcement_published') {
    return { icon: 'pi pi-megaphone', bg: '#fee2e2', color: '#dc2626' };
  }
  return { icon: 'pi pi-bell', bg: '#f3f4f6', color: '#4b5563' };
};

const formatRelative = (iso: string): string => {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = Math.max(0, now - d.getTime());
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'Upravo sada';
    const min = Math.floor(sec / 60);
    if (min < 60) return `Pre ${min} min`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Pre ${hr} ${hr === 1 ? 'sat' : hr < 5 ? 'sata' : 'sati'}`;
    const day = Math.floor(hr / 24);
    if (day === 1) return 'Juče';
    if (day < 7) return `Pre ${day} dana`;
    if (day < 30) {
      const w = Math.floor(day / 7);
      return `Pre ${w} ${w === 1 ? 'nedelje' : 'nedelja'}`;
    }
    return d.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const userInitials = (u: TaskNotification['assignedBy']): string => {
  if (!u) return '?';
  const a = (u.firstName || '').trim();
  const b = (u.lastName || '').trim();
  const init = `${a[0] || ''}${b[0] || ''}`.toUpperCase();
  return init || (u.username || '?').slice(0, 2).toUpperCase();
};

const userFullName = (u: TaskNotification['assignedBy']): string => {
  if (!u) return 'Sistem';
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || 'Sistem';
};

const TaskNotifications: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [menuTargetId, setMenuTargetId] = useState<string | null>(null);
  const menuRef = useRef<Menu>(null);

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const data = await taskNotificationsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit
      });
      setNotifications(data.notifications);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination.total,
        pages: data.pagination.pages
      }));
    } catch (err: any) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await taskNotificationsAPI.markAllAsRead();
      window.dispatchEvent(new Event('task-notifications-updated'));
      setPagination((prev) => ({ ...prev, page: 1 }));
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri označavanju notifikacija');
    }
  };

  const handleDeleteAll = async () => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete sve notifikacije?',
      async () => {
        try {
          await taskNotificationsAPI.deleteAll();
          window.dispatchEvent(new Event('task-notifications-updated'));
          setPagination((prev) => ({ ...prev, page: 1 }));
        } catch (err: any) {
          console.error('Error deleting all notifications:', err);
          showCustomAlert.error(err.response?.data?.message || 'Greška pri brisanju notifikacija');
        }
      },
      'Potvrda brisanja'
    );
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await taskNotificationsAPI.markAsRead(id);
      window.dispatchEvent(new Event('task-notifications-updated'));
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
    } catch (err: any) {
      console.error('Error marking as read:', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await taskNotificationsAPI.delete(id);
      window.dispatchEvent(new Event('task-notifications-updated'));
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1)
      }));
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      showCustomAlert.error(err.response?.data?.message || 'Greška pri brisanju notifikacije');
    }
  };

  const handleView = async (notification: TaskNotification) => {
    if (!notification.isRead) {
      try {
        await taskNotificationsAPI.markAsRead(notification._id);
        window.dispatchEvent(new Event('task-notifications-updated'));
        setNotifications((prev) =>
          prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n))
        );
      } catch (err: any) {
        console.error('Error marking notification as read on view:', err);
      }
    }

    if (notification.taskId && notification.projectId) {
      navigate(`/projects/${notification.projectId._id}`);
    }
  };

  const filteredNotifications = notifications.filter(
    (n) =>
      n.message.toLowerCase().includes(filter.toLowerCase()) ||
      n.taskId?.name?.toLowerCase().includes(filter.toLowerCase()) ||
      n.projectId?.name?.toLowerCase().includes(filter.toLowerCase())
  );

  const unreadCount = filteredNotifications.filter((n) => !n.isRead).length;

  /* ----------------- Desktop: existing table view ----------------- */
  const typeBodyTemplate = (rowData: TaskNotification) => (
    <Tag value={typeLabels[rowData.type] || rowData.type} severity={typeSeverity[rowData.type] || 'info'} />
  );

  const dateBodyTemplate = (rowData: TaskNotification) => {
    const date = new Date(rowData.createdAt);
    return `${date.toLocaleDateString('sr-RS')} ${date.toLocaleTimeString('sr-RS', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const actionsBodyTemplate = (rowData: TaskNotification) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      {rowData.taskId && rowData.projectId && (
        <Button
          icon="pi pi-eye"
          className="p-button-text"
          onClick={() => handleView(rowData)}
          aria-label="Pogledaj"
        />
      )}
      {!rowData.isRead && (
        <Button
          icon="pi pi-check"
          className="p-button-text"
          onClick={() => handleMarkAsRead(rowData._id)}
          aria-label="Označi kao pročitano"
          tooltip="Označi kao pročitano"
        />
      )}
      <Button
        icon="pi pi-trash"
        className="p-button-text"
        onClick={() => handleDelete(rowData._id)}
        aria-label="Obriši"
      />
    </div>
  );

  /* ----------------- Mobile: Facebook-style feed ----------------- */
  const renderMobileFeed = () => {
    if (loading && filteredNotifications.length === 0) {
      return (
        <div className="fb-notifications-empty">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem' }} />
          <div>Učitavanje...</div>
        </div>
      );
    }
    if (filteredNotifications.length === 0) {
      return (
        <div className="fb-notifications-empty">
          <i className="pi pi-bell" style={{ fontSize: '2rem', color: '#9ca3af' }} />
          <div className="fb-notifications-empty-title">Nema notifikacija</div>
          <div className="fb-notifications-empty-sub">Kada se nešto dogodi, javićemo vam ovde.</div>
        </div>
      );
    }

    return (
      <div className="fb-notifications-feed">
        {filteredNotifications.map((n) => {
          const ts = typeStyle(n.type);
          const fullName = userFullName(n.assignedBy);
          const subtitle = n.taskId?.name || n.projectId?.name || '';
          return (
            <div
              key={n._id}
              className={`fb-notification ${n.isRead ? '' : 'unread'}`}
              role="button"
              tabIndex={0}
              onClick={() => handleView(n)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleView(n);
              }}
            >
              <div className="fb-notification-avatar-wrap">
                <Avatar
                  label={userInitials(n.assignedBy)}
                  shape="circle"
                  size="large"
                  style={{ backgroundColor: '#e5e7eb', color: '#374151', fontWeight: 600 }}
                />
                <span className="fb-notification-badge" style={{ backgroundColor: ts.bg, color: ts.color }}>
                  <i className={ts.icon} />
                </span>
              </div>

              <div className="fb-notification-body">
                <div className="fb-notification-message">
                  <span className="fb-notification-actor">{fullName}</span>{' '}
                  <span className="fb-notification-text">{n.message}</span>
                </div>
                {subtitle && <div className="fb-notification-subtitle">{subtitle}</div>}
                <div className="fb-notification-time" style={{ color: n.isRead ? '#6b7280' : ts.color }}>
                  {formatRelative(n.createdAt)}
                </div>
              </div>

              <div className="fb-notification-side">
                {!n.isRead && <span className="fb-notification-dot" />}
                <Button
                  icon="pi pi-ellipsis-h"
                  className="fb-notification-menu-btn p-button-text p-button-rounded p-button-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuTargetId(n._id);
                    menuRef.current?.toggle(e);
                  }}
                  aria-label="Opcije"
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const targetNotification = notifications.find((n) => n._id === menuTargetId);
  const menuItems = targetNotification
    ? [
        ...(targetNotification.taskId && targetNotification.projectId
          ? [
              {
                label: 'Pogledaj',
                icon: 'pi pi-eye',
                command: () => handleView(targetNotification)
              }
            ]
          : []),
        ...(!targetNotification.isRead
          ? [
              {
                label: 'Označi kao pročitano',
                icon: 'pi pi-check',
                command: () => handleMarkAsRead(targetNotification._id)
              }
            ]
          : []),
        {
          label: 'Obriši',
          icon: 'pi pi-trash',
          command: () => handleDelete(targetNotification._id)
        }
      ]
    : [];

  return (
    <div className={`task-notifications-page ${isMobile ? 'mobile' : ''}`}>
      <Menu model={menuItems} popup ref={menuRef} appendTo={typeof document !== 'undefined' ? document.body : undefined} />

      <div className="task-notifications-header">
        <div className="task-notifications-titlebar">
          <h1 className="m-0">{isMobile ? 'Notifikacije' : 'Notifikacije zadataka'}</h1>
          {unreadCount > 0 && <span className="task-notifications-unread-count">{unreadCount}</span>}
        </div>

        <div className="task-notifications-toolbar">
          <span className="p-input-icon-left task-notifications-search">
            <i className="pi pi-search" />
            <InputText
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={isMobile ? 'Pretraga...' : 'Filtriraj notifikacije...'}
            />
          </span>

          {isMobile ? (
            <div className="task-notifications-actions">
              <Button
                icon="pi pi-check"
                className="p-button-outlined p-button-sm"
                onClick={handleMarkAllAsRead}
                disabled={pagination.total === 0 || unreadCount === 0}
                tooltip="Označi sve kao pročitano"
                aria-label="Označi sve kao pročitano"
              />
              <Button
                icon="pi pi-trash"
                className="p-button-outlined p-button-danger p-button-sm"
                onClick={handleDeleteAll}
                disabled={pagination.total === 0}
                tooltip="Obriši sve"
                aria-label="Obriši sve"
              />
            </div>
          ) : (
            <div className="task-notifications-actions">
              <Button
                label="Označi sve kao pročitane"
                icon="pi pi-check"
                onClick={handleMarkAllAsRead}
                disabled={pagination.total === 0 || unreadCount === 0}
              />
              <Button
                label="Obriši sve"
                icon="pi pi-trash"
                severity="danger"
                onClick={handleDeleteAll}
                disabled={pagination.total === 0}
              />
            </div>
          )}
        </div>
      </div>

      {isMobile ? (
        <>
          {renderMobileFeed()}
          {pagination.pages > 1 && (
            <div className="fb-notifications-pager">
              <Button
                icon="pi pi-chevron-left"
                className="p-button-text p-button-sm"
                disabled={pagination.page <= 1}
                onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                aria-label="Prethodna"
              />
              <span className="fb-notifications-pager-info">
                {pagination.page} / {pagination.pages}
              </span>
              <Button
                icon="pi pi-chevron-right"
                className="p-button-text p-button-sm"
                disabled={pagination.page >= pagination.pages}
                onClick={() => setPagination((p) => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                aria-label="Sledeća"
              />
            </div>
          )}
        </>
      ) : (
        <DataTable
          value={filteredNotifications}
          loading={loading}
          paginator
          rows={pagination.limit}
          totalRecords={pagination.total}
          first={(pagination.page - 1) * pagination.limit}
          onPage={(e) => setPagination((prev) => ({ ...prev, page: (e.page || 0) + 1 }))}
          emptyMessage="Nema notifikacija"
          rowClassName={(rowData) => (rowData.isRead ? '' : 'unread-notification')}
        >
          <Column field="type" header="Tip" body={typeBodyTemplate} sortable />
          <Column field="message" header="Poruka" sortable />
          <Column field="createdAt" header="Datum i vreme" body={dateBodyTemplate} sortable />
          <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '180px' }} />
        </DataTable>
      )}
    </div>
  );
};

export default TaskNotifications;
